import React, { useState, useEffect } from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';
import { formatUserFacingError } from '../utils/formatError';

export type ToastType = 'success' | 'error' | 'info';

export interface ToastMessage {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

type ToastListener = (toast: ToastMessage) => void;
const listeners = new Set<ToastListener>();

export const toast = {
  success: (message: string, duration = 4000) => {
    emitToast(message, 'success', duration);
  },
  error: (message: string | Error | unknown, duration = 6000) => {
    const formatted = formatUserFacingError(message);
    emitToast(formatted, 'error', duration);
  },
  info: (message: string, duration = 4000) => {
    emitToast(message, 'info', duration);
  }
};

function emitToast(message: string, type: ToastType, duration: number) {
  const newToast: ToastMessage = {
    id: 'toast-' + Math.random().toString(36).substring(2, 9),
    message,
    type,
    duration
  };
  listeners.forEach(listener => listener(newToast));
}

export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  useEffect(() => {
    const handleNewToast = (newToast: ToastMessage) => {
      setToasts(prev => [...prev.slice(-4), newToast]); // Keep up to 5 visible

      const timer = setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== newToast.id));
      }, newToast.duration || 4000);

      return () => clearTimeout(timer);
    };

    listeners.add(handleNewToast);
    return () => {
      listeners.delete(handleNewToast);
    };
  }, []);

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[99999] flex flex-col gap-2.5 max-w-sm sm:max-w-md w-full pointer-events-none px-4 sm:px-0 select-none">
      {toasts.map(t => (
        <div
          key={t.id}
          className={`pointer-events-auto flex items-center gap-3 px-4 py-3.5 rounded-2xl shadow-2xl text-xs transition-all transform animate-slide-up ${
            t.type === 'error'
              ? 'bg-rose-600 text-white shadow-rose-950/20 border border-rose-400/30'
              : 'bg-[#0050ff] text-white shadow-blue-950/25 border border-blue-400/30'
          }`}
        >
          {t.type === 'error' ? (
            <div className="w-6 h-6 rounded-full bg-white/20 text-white flex items-center justify-center shrink-0">
              <AlertCircle className="w-3.5 h-3.5 text-white" />
            </div>
          ) : (
            <div className="w-6 h-6 rounded-full bg-white/20 text-white flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-3.5 h-3.5 text-white" />
            </div>
          )}

          <div className="flex-1 leading-snug break-words text-white font-medium">{t.message}</div>

          <button
            type="button"
            onClick={() => removeToast(t.id)}
            className="text-white/75 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10 cursor-pointer shrink-0"
            title="Luk"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}
